// SlicerConfigModal.jsx
// Small dedicated config modal for slicer panels. Deliberately separate from
// the giant PanelConfigModal — slicers only need db / table / column /
// widget type / title, nothing else.

import React, { useEffect, useState } from 'react';
import { X, Filter, Database, ChevronDown, Search, Calendar, List } from 'lucide-react';
import questdbService from '../../services/questdb';
import mysqlService from '../../services/mysql';
import reportService from '../../services/reportService';
import { inheritedInstanceId, plantInstances } from '../../services/mysqlSource.js';

const WIDGET_TYPES = [
  { id: 'dropdown',  label: 'Dropdown',   icon: ChevronDown, hint: 'Pick one value from a list' },
  { id: 'multi',     label: 'Multi-select', icon: List,      hint: 'Pick any number of values (IN filter)' },
  { id: 'daterange', label: 'Date range', icon: Calendar,    hint: 'Two date pickers (BETWEEN filter)' },
  { id: 'search',    label: 'Search box', icon: Search,      hint: 'Free-text search (LIKE filter)' }
];

export default function SlicerConfigModal({
  isOpen, onClose, onSave, editingSlicer = null, plantId, darkMode = true, panels = []
}) {
  const isEdit = !!editingSlicer;

  const [title, setTitle] = useState('');
  // No default engine — force the user to pick, so we never fire a bogus
  // QuestDB / MySQL request on mount that produces a misleading error.
  const [dbEngine, setDbEngine] = useState('');
  const [mysqlDatabase, setMysqlDatabase] = useState('');
  // MySQL data source: a slicer is a panel, so it gets the panel picker,
  // pre-filled with the source the dashboard's panels / the plant use.
  const [mysqlInstanceId, setMysqlInstanceId] = useState('');
  const [instances, setInstances] = useState([]);
  const [table, setTable] = useState('');
  const [column, setColumn] = useState('');
  const [widgetType, setWidgetType] = useState('dropdown');

  const [databases, setDatabases] = useState([]);
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState({ db: false, tbl: false, col: false });
  const [loadError, setLoadError] = useState(''); // surface any API failures instead of silently blanking dropdowns

  // Seed form when editing.
  useEffect(() => {
    if (!isOpen) return;
    if (editingSlicer) {
      setTitle(editingSlicer.title || '');
      setDbEngine(editingSlicer.dbEngine || '');
      setMysqlDatabase(editingSlicer.mysqlDatabase || '');
      setMysqlInstanceId(editingSlicer.mysqlInstanceId || '');
      setTable(editingSlicer.table || '');
      setColumn(editingSlicer.column || '');
      setWidgetType(editingSlicer.widgetType || 'dropdown');
    } else {
      // Fresh add — clear all fields AND any stale api errors/lists
      setTitle(''); setDbEngine(''); setMysqlDatabase(''); setMysqlInstanceId('');
      setTable(''); setColumn(''); setWidgetType('dropdown');
      setTables([]); setColumns([]); setDatabases([]);
      setLoadError('');
    }
  }, [isOpen, editingSlicer]);

  // The plant's data sources; pre-fill the inherited one.
  useEffect(() => {
    if (!isOpen || dbEngine !== 'mysql') return;
    let cancelled = false;
    plantInstances(plantId)
      .then(list => {
        if (cancelled) return;
        setInstances(list);
        setMysqlInstanceId(cur => cur || inheritedInstanceId(panels, list));
      })
      .catch(() => { if (!cancelled) setInstances([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dbEngine, plantId]);

  const mysqlTarget = { instanceId: mysqlInstanceId, plantId };

  // Load MySQL databases for the chosen data source.
  useEffect(() => {
    if (!isOpen) return;
    setLoadError('');
    if (dbEngine !== 'mysql') return;
    if (!mysqlInstanceId) { setDatabases([]); return; }
    setLoading(l => ({ ...l, db: true }));
    mysqlService.getDatabases(mysqlTarget)
      .then(res => {
        const dbs = (res || []).map(r => Object.values(r)[0])
          .filter(d => !['information_schema', 'performance_schema', 'mysql', 'sys'].includes(d));
        setDatabases(dbs);
      })
      .catch(err => {
        setDatabases([]);
        setLoadError(`Databases: ${err?.response?.data?.error || err?.message || 'failed to load'}`);
      })
      .finally(() => setLoading(l => ({ ...l, db: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dbEngine, mysqlInstanceId, plantId]);

  // Load tables when db or engine changes. Reset tables/columns proactively
  // so a stale list from a previous db never lingers on screen.
  useEffect(() => {
    if (!isOpen) return;
    setTables([]); setColumns([]);
    setLoadError('');                                    // clear stale errors on any transition
    if (!dbEngine) return;                               // engine not yet chosen
    if (dbEngine === 'mysql' && !mysqlDatabase) return;
    if (dbEngine === 'questdb' && !plantId) return;
    setLoading(l => ({ ...l, tbl: true }));
    const p = dbEngine === 'mysql'
      ? mysqlService.getTables(mysqlDatabase, mysqlTarget).then(r => (r || []).map(x => Object.values(x)[0]).filter(Boolean))
      : questdbService.getTables(plantId).then(r => (r || []).map(x => x.table_name).filter(Boolean));
    p.then(list => {
      console.log('[SlicerConfig] tables loaded:', { dbEngine, mysqlDatabase, plantId, count: list.length });
      setTables(list);
      if (!list.length) setLoadError(`No tables found in ${dbEngine === 'mysql' ? mysqlDatabase : plantId}.`);
    })
      .catch(err => {
        console.error('[SlicerConfig] tables error:', err);
        setTables([]);
        setLoadError(`Tables: ${err?.response?.data?.error || err?.message || 'failed to load'}`);
      })
      .finally(() => setLoading(l => ({ ...l, tbl: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dbEngine, mysqlInstanceId, mysqlDatabase, plantId]);

  // Load columns when table changes.
  useEffect(() => {
    if (!isOpen) return;
    setLoadError('');
    if (!table) { setColumns([]); return; }
    setLoading(l => ({ ...l, col: true }));
    reportService.getColumns(plantId || '_', table, { dbEngine, mysqlDatabase, mysqlInstanceId: dbEngine === 'mysql' ? mysqlInstanceId : undefined })
      .then(cols => setColumns((cols || []).map(c => c.name || c)))
      .catch(err => {
        setColumns([]);
        setLoadError(`Columns: ${err?.response?.data?.error || err?.message || 'failed to load'}`);
      })
      .finally(() => setLoading(l => ({ ...l, col: false })));
  }, [isOpen, table, dbEngine, mysqlInstanceId, mysqlDatabase, plantId]);

  const canSave = table && column && widgetType && (dbEngine !== 'mysql' || mysqlInstanceId);

  const handleSave = () => {
    if (!canSave) return;
    const payload = {
      ...(editingSlicer || {}),
      type: 'slicer',
      title: title.trim() || `${column} filter`,
      dbEngine, mysqlDatabase: dbEngine === 'mysql' ? mysqlDatabase : '',
      mysqlInstanceId: dbEngine === 'mysql' ? mysqlInstanceId : '',
      table, column, widgetType,
      // Dashboard schema uses `width`/`height` (not w/h). Sensible defaults
      // in grid units — multi-select needs more vertical room for the list.
      width:  editingSlicer?.width  ?? 3,
      height: editingSlicer?.height ?? (widgetType === 'multi' ? 6 : 3)
    };
    onSave(payload);
    onClose();
  };

  if (!isOpen) return null;

  const T = darkMode ? {
    bg: 'rgba(4,7,14,0.72)', card: '#111827', ink: '#f3f4f6', mute: '#9ca3af',
    line: '#1f2937', accent: '#8b5cf6', field: '#0b1220'
  } : {
    bg: 'rgba(15,23,42,0.35)', card: '#ffffff', ink: '#0f172a', mute: '#64748b',
    line: '#e2e8f0', accent: '#7c3aed', field: '#f8fafc'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: T.bg, zIndex: 9700,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24
    }} onClick={onClose}>
      <div style={{
        width: 'min(520px, 100%)', background: T.card, borderRadius: 12,
        border: `1px solid ${T.line}`, boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh'
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', gap: 8
        }}>
          <Filter size={16} color={T.accent} />
          <div style={{ fontWeight: 700, color: T.ink, fontSize: 14, flex: 1 }}>
            {isEdit ? 'Edit slicer' : 'Add slicer'}
          </div>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: T.mute, cursor: 'pointer', padding: 4
          }}><X size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Title (optional)" T={T}>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder={column ? `${column} filter` : 'e.g. Region filter'}
              style={inp(T)} />
          </Field>

          <Field label="Database" T={T}>
            <div style={{ display: 'flex', gap: 6 }}>
              {['questdb', 'mysql'].map(k => (
                <button key={k}
                  onClick={() => { setDbEngine(k); setMysqlDatabase(''); setMysqlInstanceId(''); setTable(''); setColumn(''); }}
                  style={{
                    flex: 1, padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    border: `1px solid ${dbEngine === k ? T.accent : T.line}`,
                    background: dbEngine === k ? 'rgba(139,92,246,0.12)' : 'transparent',
                    color: dbEngine === k ? T.accent : T.ink, cursor: 'pointer'
                  }}>
                  {k === 'questdb' ? 'QuestDB (live)' : 'MySQL / MariaDB'}
                </button>
              ))}
            </div>
            {!dbEngine && (
              <div style={{ marginTop: 4, fontSize: 10, color: T.mute }}>
                Pick a source — nothing is queried until you choose.
              </div>
            )}
          </Field>

          {dbEngine === 'mysql' && (
            <Field label="MySQL data source" T={T}>
              <select data-testid="slicer-mysql-instance" value={mysqlInstanceId}
                onChange={e => { setMysqlInstanceId(e.target.value); setMysqlDatabase(''); setTable(''); setColumn(''); }}
                style={inp(T)}>
                <option value="">— select data source —</option>
                {instances.map(inst => (
                  <option key={inst.instanceId} value={inst.instanceId}>{inst.label || inst.instanceId}{inst.isReadReplica ? ' (read replica)' : ''}</option>
                ))}
                {mysqlInstanceId && !instances.some(i => i.instanceId === mysqlInstanceId) && (
                  <option value={mysqlInstanceId}>{mysqlInstanceId} (unavailable)</option>
                )}
              </select>
            </Field>
          )}

          {dbEngine === 'mysql' && (
            <Field label={loading.db ? 'MySQL database (loading…)' : 'MySQL database'} T={T}>
              <select value={mysqlDatabase} onChange={e => { setMysqlDatabase(e.target.value); setTable(''); setColumn(''); }}
                style={inp(T)} disabled={loading.db || !mysqlInstanceId}>
                <option value="">{mysqlInstanceId ? '— select database —' : 'pick a data source first'}</option>
                {databases.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          )}

          <Field label={loading.tbl ? 'Table (loading…)' : `Table${tables.length ? ` (${tables.length})` : ''}`} T={T}>
            <select value={table} onChange={e => { setTable(e.target.value); setColumn(''); }}
              style={inp(T)} disabled={loading.tbl}>
              <option value="">
                {loading.tbl ? 'loading…'
                  : tables.length ? '— select table —'
                  : (dbEngine === 'mysql' && !mysqlDatabase) ? 'pick a database first'
                  : (dbEngine === 'questdb' && !plantId) ? 'no plant selected'
                  : 'no tables available'}
              </option>
              {tables.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>

          <Field label={loading.col ? 'Column (loading…)' : `Column to filter on${columns.length ? ` (${columns.length})` : ''}`} T={T}>
            <select value={column} onChange={e => setColumn(e.target.value)}
              style={inp(T)} disabled={loading.col}>
              <option value="">
                {loading.col ? 'loading…'
                  : columns.length ? '— select column —'
                  : !table ? 'pick a table first' : 'no columns available'}
              </option>
              {columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          {loadError && (
            <div style={{
              padding: '8px 10px', background: 'rgba(239,68,68,0.10)',
              border: '1px solid rgba(239,68,68,0.35)', borderRadius: 6,
              color: '#fca5a5', fontSize: 11, lineHeight: 1.4
            }}>
              {loadError}
            </div>
          )}

          <Field label="Widget type" T={T}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {WIDGET_TYPES.map(w => {
                const active = widgetType === w.id;
                const Icon = w.icon;
                return (
                  <button key={w.id} onClick={() => setWidgetType(w.id)}
                    style={{
                      padding: '10px 12px', borderRadius: 6, textAlign: 'left',
                      border: `1px solid ${active ? T.accent : T.line}`,
                      background: active ? 'rgba(139,92,246,0.10)' : 'transparent',
                      color: T.ink, cursor: 'pointer',
                      display: 'flex', flexDirection: 'column', gap: 4
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
                      <Icon size={13} color={active ? T.accent : T.mute} /> {w.label}
                    </div>
                    <div style={{ fontSize: 10, color: T.mute, lineHeight: 1.3 }}>{w.hint}</div>
                  </button>
                );
              })}
            </div>
          </Field>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${T.line}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8
        }}>
          <button onClick={onClose} style={{
            padding: '8px 14px', borderRadius: 6, border: `1px solid ${T.line}`,
            background: 'transparent', color: T.ink, cursor: 'pointer', fontSize: 12, fontWeight: 500
          }}>Cancel</button>
          <button onClick={handleSave} disabled={!canSave} style={{
            padding: '8px 16px', borderRadius: 6, border: 'none',
            background: canSave ? T.accent : T.line,
            color: '#fff', cursor: canSave ? 'pointer' : 'not-allowed',
            fontSize: 12, fontWeight: 600, opacity: canSave ? 1 : 0.6
          }}>{isEdit ? 'Save changes' : 'Add slicer'}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, T, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 10, color: T.mute, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</span>
      {children}
    </label>
  );
}

function inp(T) {
  return {
    width: '100%', padding: '8px 10px',
    background: T.field, color: T.ink,
    border: `1px solid ${T.line}`, borderRadius: 6, fontSize: 12, outline: 'none'
  };
}
